/**
 * Set when this tab reloads over a page it could not load, until a page loads again: one reload, never a
 * loop. The app clears the session storage on unload (App.tsx): this key is kept.
 */
export const STALE_CHUNK_RELOAD_KEY = 'jxt-stale-chunk-reloaded';

const hasReloaded = (): boolean => {
  try {
    return sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) !== null;
  } catch {
    return false;
  }
};

const markReloaded = () => {
  try {
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, '1');
  } catch {
    // Without storage, no guard: the next failure goes to the error boundary instead of reloading.
  }
};

/** A page loaded: a later failure is a new deploy again, worth a reload. */
export const forgetStaleChunkReload = () => {
  try {
    sessionStorage.removeItem(STALE_CHUNK_RELOAD_KEY);
  } catch {
    // Nothing to forget.
  }
};

/**
 * A page's chunk that fails to load: most likely, a deploy replaced the version this tab runs (its
 * chunks are deleted, the server answers index.html instead). Reloads the tab once, on the new version,
 * staying suspended until then — no error page in between. Failing again before any page loads (offline,
 * a real error), however long the reload took: rethrown, to the error boundary.
 */
export const reloadOnStaleChunk = (error: unknown): Promise<never> => {
  if (hasReloaded()) {
    return Promise.reject(error);
  }

  markReloaded();
  // Without storage, the guard cannot hold across the reload: no reload, rather than a loop.
  if (!hasReloaded()) {
    return Promise.reject(error);
  }

  window.location.reload();

  return new Promise<never>(() => undefined);
};
